import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { AuthRedirectService } from "./auth-redirect.service";
import { usesFootballerAuth } from './footballer-auth.service';

export interface LoginIdentityMigrationResponse {
  id: string;
  migrationToken?: string;
  status: string;
  sourceProvider: string;
  targetProvider: string;
  targetConnection?: string;
  targetLoginEmail?: string;
  expiresAt: string;
  profileAccessTransferred: boolean;
  cleanupPending: boolean;
  authorizationParameters?: { [key: string]: string };
}

@Injectable({
  providedIn: "root"
})
export class LoginIdentityMigrationService {
  private static readonly tokenKey = "nogosh.loginIdentityMigration.token";
  private readonly apiUrl = environment.API_BASE_URL + "/player/me/login-identity-migrations";

  constructor(
    private authHttp: HttpClient,
    private authRedirect: AuthRedirectService
  ) {}

  public beginPasswordMigration(targetUrl: string): Observable<void> {
    return this.authHttp.post<LoginIdentityMigrationResponse>(`${this.apiUrl}/password`, {}).pipe(
      tap((migration) => this.remember(migration)),
      switchMap((migration) => this.authRedirect.passwordMigration(targetUrl, migration.authorizationParameters))
    );
  }

  public completePendingMigration(): Observable<LoginIdentityMigrationResponse | null> {
    if (usesFootballerAuth) { this.clear(); return of(null); }
    const migrationToken = this.pendingToken();
    if (!migrationToken) {
      return of(null);
    }

    return this.authHttp.post<LoginIdentityMigrationResponse>(`${this.apiUrl}/password/complete`, {
      migrationToken
    }).pipe(
      tap((migration) => {
        if (!migration.profileAccessTransferred) {
          throw new Error("Login identity migration is not complete.");
        }
        this.clear();
      })
    );
  }

  public hasPendingMigration(): boolean {
    return !!this.pendingToken();
  }

  private remember(migration: LoginIdentityMigrationResponse): void {
    if (!migration || !migration.migrationToken) {
      return;
    }
    sessionStorage.setItem(LoginIdentityMigrationService.tokenKey, migration.migrationToken);
  }

  private pendingToken(): string {
    try {
      return sessionStorage.getItem(LoginIdentityMigrationService.tokenKey) || "";
    } catch (_) {
      return "";
    }
  }

  private clear(): void {
    try {
      sessionStorage.removeItem(LoginIdentityMigrationService.tokenKey);
    } catch (_) {
    }
  }
}
